import * as React from 'react'
import { View, StyleSheet } from 'react-native'
import { Button, HelperText, Text, TextInput } from 'react-native-paper'
import moment from 'moment'
import Toast from 'react-native-root-toast'
import { useTranslation } from 'react-i18next'
import { MelddModal } from '../../../@ui/containers/modals'
import {
  useCreateCouponMutation,
  useUpdateCouponMutation,
  type CouponsQuery,
} from '../../../generated/graphql'
import { MessageType } from '../../../utils/message-type'

export type AdminCouponRow = CouponsQuery['coupons'][number]

type EditCouponModalProps = {
  visible: boolean
  /** When null and visible, modal is in create mode (server generates code). */
  coupon: AdminCouponRow | null
  onDismiss: () => void
  onSaved: () => void
}

export function EditCouponModal({
  visible,
  coupon,
  onDismiss,
  onSaved,
}: EditCouponModalProps) {
  const { t } = useTranslation('admin')
  const isCreate = !coupon
  const [adminLabel, setAdminLabel] = React.useState('')
  const [maxRedeems, setMaxRedeems] = React.useState('')
  const [appliedMonths, setAppliedMonths] = React.useState('')
  const [discountPercentage, setDiscountPercentage] = React.useState('')
  const [validToInput, setValidToInput] = React.useState('')
  const [localError, setLocalError] = React.useState('')
  const [createCoupon, { loading: creating }] = useCreateCouponMutation()
  const [updateCoupon, { loading: updating }] = useUpdateCouponMutation()
  const loading = creating || updating

  React.useEffect(() => {
    if (!visible) return

    setAdminLabel(coupon?.adminLabel ?? '')
    setMaxRedeems(coupon ? String(coupon.maxRedeems) : '50')
    setAppliedMonths(coupon ? String(coupon.appliedMonths) : '0')
    setDiscountPercentage(coupon ? String(coupon.discountPercentage) : '100')
    setValidToInput(
      coupon
        ? moment(coupon.validTo).format('DD/MM/YYYY')
        : moment().add(1, 'year').format('DD/MM/YYYY')
    )
    setLocalError('')
  }, [coupon, visible])

  const handleSave = async () => {
    if (!visible) return
    setLocalError('')
    const max = parseInt(maxRedeems, 10)
    const months = parseInt(appliedMonths, 10)
    const discount = parseInt(discountPercentage, 10)
    if (Number.isNaN(max) || max < 1) {
      setLocalError(t('edit-error-max-invalid'))
      return
    }
    if (Number.isNaN(months) || months < 0 || months > 12) {
      setLocalError(t('edit-error-applied-months-invalid'))
      return
    }
    if (Number.isNaN(discount) || discount < 0 || discount > 100) {
      setLocalError(t('edit-error-discount-invalid'))
      return
    }
    if (coupon && max < coupon.redeemCount) {
      setLocalError(
        t('edit-error-max-below-redeems', { min: coupon.redeemCount })
      )
      return
    }
    const parsed = moment(validToInput, 'DD/MM/YYYY', true).endOf('day')
    if (!parsed.isValid()) {
      setLocalError(t('edit-error-date-invalid'))
      return
    }
    if (!parsed.isAfter(moment())) {
      setLocalError(t('edit-error-date-future'))
      return
    }
    const trimmedLabel = adminLabel.trim()
    if (trimmedLabel.length > 160) {
      setLocalError(t('edit-error-admin-label-length'))
      return
    }
    try {
      if (isCreate) {
        const { data } = await createCoupon({
          variables: {
            input: {
              maxRedeems: max,
              appliedMonths: months,
              discountPercentage: discount,
              validTo: parsed.toDate(),
              ...(trimmedLabel ? { adminLabel: trimmedLabel } : {}),
            },
          },
        })
        const code = data?.createCoupon?.code
        Toast.show(
          code ? t('create-saved', { code }) : t('create-saved-generic'),
          MessageType.info
        )
      } else if (coupon) {
        await updateCoupon({
          variables: {
            input: {
              id: coupon.id,
              maxRedeems: max,
              appliedMonths: months,
              discountPercentage: discount,
              validTo: parsed.toDate(),
              adminLabel: trimmedLabel,
            },
          },
        })
        Toast.show(t('edit-saved'), MessageType.info)
      }
      onSaved()
      onDismiss()
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : isCreate
          ? t('create-error-generic')
          : t('edit-error-generic')
      Toast.show(msg, MessageType.error)
    }
  }

  if (!visible) {
    return null
  }

  return (
    <MelddModal
      visible={visible}
      onDismiss={onDismiss}
      title={
        isCreate ? t('create-title') : t('edit-title', { code: coupon.code })
      }
      scrollable
      maxHeight="90%"
      body={
        <View style={styles.body}>
          {isCreate ? (
            <Text variant="bodySmall" style={styles.help}>
              {t('create-help-code-auto')}
            </Text>
          ) : null}
          <TextInput
            label={t('field-admin-label')}
            mode="outlined"
            value={adminLabel}
            onChangeText={(v) => {
              setAdminLabel(v)
              if (localError) setLocalError('')
            }}
            placeholder={t('field-admin-label-placeholder')}
            maxLength={160}
            autoCapitalize="sentences"
            style={styles.input}
          />
          <Text variant="bodySmall" style={styles.help}>
            {t('edit-help-admin-label')}
          </Text>
          <TextInput
            label={t('field-max-redeems')}
            mode="outlined"
            keyboardType="number-pad"
            value={maxRedeems}
            onChangeText={(v) => {
              setMaxRedeems(v)
              if (localError) setLocalError('')
            }}
            style={styles.input}
          />
          <TextInput
            label={t('field-applied-months')}
            mode="outlined"
            keyboardType="number-pad"
            value={appliedMonths}
            onChangeText={(v) => {
              setAppliedMonths(v)
              if (localError) setLocalError('')
            }}
            style={styles.input}
          />
          <Text variant="bodySmall" style={styles.help}>
            {t('edit-help-applied-months')}
          </Text>
          <TextInput
            label={t('field-discount-percentage')}
            mode="outlined"
            keyboardType="number-pad"
            value={discountPercentage}
            onChangeText={(v) => {
              setDiscountPercentage(v)
              if (localError) setLocalError('')
            }}
            style={styles.input}
          />
          <Text variant="bodySmall" style={styles.help}>
            {t('edit-help-discount-percentage')}
          </Text>

          <TextInput
            label={t('field-valid-to-date')}
            mode="outlined"
            placeholder="DD/MM/YYYY"
            value={validToInput}
            onChangeText={(v) => {
              setValidToInput(v)
              if (localError) setLocalError('')
            }}
            autoCapitalize="none"
            keyboardType="numbers-and-punctuation"
            style={styles.input}
          />
          <Text variant="bodySmall" style={styles.help}>
            {t('edit-help-date-ddmmyyyy')}
          </Text>
          <HelperText type="error" visible={!!localError}>
            {localError}
          </HelperText>
        </View>
      }
      actions={
        <View style={styles.actions}>
          <Button mode="outlined" onPress={onDismiss} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button mode="contained" onPress={handleSave} loading={loading}>
            {isCreate ? t('create-submit') : t('save')}
          </Button>
        </View>
      }
    />
  )
}

const styles = StyleSheet.create({
  body: { gap: 12, width: '100%' },
  help: { opacity: 0.75 },
  input: { width: '100%' },
  actions: { flexDirection: 'row', gap: 12, justifyContent: 'flex-end' },
})
